import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Download, Share, ArrowLeft, MessageCircle } from 'lucide-react';
import { supabase } from '../../lib/supabase';
import { useAuth } from '../../contexts/AuthContext';
import { generatePdfReport } from '../../lib/pdf-generator';
import Button from '../../components/ui/Button';
import Card from '../../components/ui/Card';
import { trackEvent, trackException } from '../../lib/analytics';

const ReadingResult: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const { userProfile } = useAuth();
  const navigate = useNavigate();
  const [reading, setReading] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [downloadingPdf, setDownloadingPdf] = useState(false);
  const [pdfUrl, setPdfUrl] = useState<string | null>(null);

  useEffect(() => {
    const fetchReading = async () => {
      if (!id) return;

      try {
        const { data, error } = await supabase
          .from('readings')
          .select('*')
          .eq('reading_id', id)
          .single();

        if (error) throw error;
        
        setReading(data);

        trackEvent('reading_viewed', 'view', data.reading_type);
        trackEvent('user_engagement', 'reading_result_viewed', data.reading_type);

        // Generate PDF on load
        if (data && userProfile) {
          const pdfDataUrl = generatePdfReport({
            userName: userProfile.name,
            readingType: data.reading_type,
            predictionText: data.prediction_text,
            birthDetails: {
              dob: userProfile.birth_date,
              birthTime: userProfile.birth_time,
              birthPlace: `${userProfile.birth_location.city}, ${userProfile.birth_location.country}`,
            },
          });
          setPdfUrl(pdfDataUrl);
          
          trackEvent('pdf_generation', 'success', data.reading_type);
        }
      } catch (err: any) {
        console.error('Error fetching reading:', err);
        setError('Failed to load your reading. Please try again.');
        
        trackEvent('reading_viewed', 'error', 'fetch_failed');
        trackException(`Reading fetch failed: ${err.message}`, false);
      } finally {
        setLoading(false);
      }
    };

    fetchReading();
  }, [id, userProfile]);

  const handleDownloadPdf = () => {
    if (!pdfUrl) return;

    setDownloadingPdf(true);
    
    try {
      trackEvent('pdf_download', 'download', reading.reading_type);
      trackEvent('user_engagement', 'pdf_downloaded', reading.reading_type);
      
      // Create a link and trigger download
      const link = document.createElement('a');
      link.href = pdfUrl;
      link.download = `${reading.reading_type}_reading_${new Date().toISOString().split('T')[0]}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch (err: any) {
      console.error('Error downloading PDF:', err);
      setError('Failed to download PDF. Please try again.');
      
      trackEvent('pdf_download', 'error', reading.reading_type);
      trackException(`PDF download failed: ${err.message}`, false);
    } finally {
      setDownloadingPdf(false);
    }
  };

  const handleShareWhatsApp = () => {
    if (!pdfUrl) return;

    trackEvent('reading_share', 'share', 'whatsapp');
    trackEvent('user_engagement', 'reading_shared', reading.reading_type);

    const readingTitle = `${reading?.reading_type.charAt(0).toUpperCase() + reading?.reading_type.slice(1)} Reading from AI Pandit Ji`;
    const text = `Check out my ${readingTitle}! Get your own reading at https://theaipanditji.com\n\nHere's my reading report:`;
    
    // Create a blob from the PDF data URL
    const byteString = atob(pdfUrl.split(',')[1]);
    const mimeString = pdfUrl.split(',')[0].split(':')[1].split(';')[0];
    const ab = new ArrayBuffer(byteString.length);
    const ia = new Uint8Array(ab);
    for (let i = 0; i < byteString.length; i++) {
      ia[i] = byteString.charCodeAt(i);
    }
    const blob = new Blob([ab], { type: mimeString });
    
    // Create a File object from the blob
    const file = new File([blob], `${reading.reading_type}_reading.pdf`, { type: 'application/pdf' });

    // Share via Web Share API if supported
    if (navigator.share && navigator.canShare && navigator.canShare({ files: [file] })) {
      navigator.share({
        title: readingTitle,
        text: text,
        files: [file]
      }).then(() => {
        trackEvent('reading_share', 'success', 'native_share');
      }).catch(error => {
        console.log('Error sharing:', error);
        trackEvent('reading_share', 'fallback', 'native_share_failed');
        // Fallback to regular WhatsApp share
        window.open(`https://wa.me/?text=${encodeURIComponent(text)}`, '_blank');
      });
    } else {
      // Fallback to regular WhatsApp share
      window.open(`https://wa.me/?text=${encodeURIComponent(text)}`, '_blank');
      trackEvent('reading_share', 'success', 'whatsapp_web');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-neutral-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  if (error || !reading) {
    return (
      <div className="min-h-screen bg-neutral-50 py-12 px-4">
        <div className="max-w-3xl mx-auto text-center">
          <div className="bg-error-50 text-error-600 p-6 rounded-lg">
            <h2 className="text-xl font-semibold mb-2">Error Loading Reading</h2>
            <p>{error || 'Reading not found'}</p>
            <Button 
              onClick={() => {
                trackEvent('error_recovery', 'click', 'reading_not_found');
                navigate('/');
              }} 
              variant="outline" 
              className="mt-4"
            >
              Back to Dashboard
            </Button>
          </div>
        </div>
      </div>
    );
  }

  // Format the reading type for display
  const readingTypeFormatted = reading.reading_type.charAt(0).toUpperCase() + reading.reading_type.slice(1);

  return (
    <div className="min-h-screen bg-neutral-50 py-8 px-4">
      <div className="max-w-3xl mx-auto">
        <Button 
          variant="ghost" 
          size="sm"
          onClick={() => {
            trackEvent('navigation', 'back_from_reading', reading.reading_type);
            navigate(-1);
          }}
          className="mb-6" 
          leftIcon={<ArrowLeft size={16} />}
        >
          Back
        </Button>

        <div className="text-center mb-8">
          <motion.h1 
            className="text-3xl font-bold text-primary-800 font-display"
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
          >
            {readingTypeFormatted} Reading
          </motion.h1>
          <p className="mt-2 text-neutral-600">
            Your celestial guidance based on your birth chart
          </p>
        </div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.2 }}
        >
          <Card className="relative overflow-hidden">
            <div className="absolute top-0 right-0 left-0 h-4 bg-gradient-to-r from-primary-400 via-primary-600 to-secondary-500"></div>
            <div className="pt-8 px-6 pb-6">
              {/* Reading content */}
              <div className="prose max-w-none">
                {reading.prediction_text.split('\n\n').map((paragraph: string, index: number) => (
                  <p 
                    key={index}
                    className={index === 0 ? "text-lg font-medium mb-4" : "mb-4"}
                  >
                    {paragraph}
                  </p>
                ))}
              </div>
              
              {/* Action buttons */}
              <div className="mt-8 flex flex-wrap gap-4">
                <Button 
                  onClick={handleDownloadPdf}
                  isLoading={downloadingPdf}
                  leftIcon={<Download size={16} />}
                >
                  Download PDF
                </Button>
                
                <Button 
                  variant="outline"
                  onClick={handleShareWhatsApp}
                  leftIcon={<MessageCircle size={16} />}
                  className="bg-[#25D366] hover:bg-[#128C7E] text-white border-none"
                >
                  Share on WhatsApp
                </Button>
              </div>
            </div>
          </Card>
        </motion.div>
      </div>
    </div>
  );
};

export default ReadingResult;